import React, { useState } from 'react';
import './footer.css';
import { useKeyboardPin } from './useKeyboardPin';

/** Which tier a row belongs to within a `FooterDock`. */
export type FooterDockRowPin = 'bottom' | 'keyboard';

export interface FooterDockRowProps {
  children: React.ReactNode;
  /**
   * Which tier this row belongs to:
   * - `'bottom'` (default) — docked to the screen bottom; stays put and is
   *   allowed to be hidden behind the keyboard.
   * - `'keyboard'` — the single row that rises above the on-screen keyboard,
   *   resting atop the bottom-pinned stack. At most one per dock. The row
   *   reconciles itself with the keyboard automatically (anchors above it,
   *   pins the document, re-syncs the iOS caret) — the client just renders it.
   */
  pin?: FooterDockRowPin;
  /**
   * Render the root as a semantic `<footer>` (a `contentinfo` landmark) instead
   * of a `<div>`. Use for a page's primary action row; leave off for furniture
   * like a floating search pill, which is not page furniture.
   */
  landmark?: boolean;
  /**
   * Render children without the default centered action-dock content box. Use for
   * content that positions itself / manages its own spacing (e.g. a search pill),
   * so it doesn't inherit the dock's max-width / bottom clearance.
   */
  bare?: boolean;
  /** Extra styles merged over the row root. */
  style?: React.CSSProperties;
  /** Extra styles merged over the inner content box. */
  contentStyle?: React.CSSProperties;
}

function isTextInput(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

interface ChromeProps {
  tier: FooterDockRowPin;
  landmark: boolean;
  bare: boolean;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  children: React.ReactNode;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  /**
   * Keyboard tier only: whether the on-screen keyboard is actually open for this
   * row. Reflected as data-kb-active so CSS lifts the row above the keyboard. It
   * tracks the keyboard opening (the viewport shrinking after a text input is
   * focused), NOT focus alone — so on a desktop browser with no soft keyboard,
   * focusing the field leaves the row at rest instead of dropping it to the
   * bottom. (Also gated on the focused element being a text input upstream, not
   * :focus-within, so in-row buttons like the device-contact picker don't lift
   * the row mid-tap.)
   */
  active?: boolean;
}

// The shared, presentational chrome: full-width row with the dock scrim and
// (unless `bare`) the centered action-dock content box.
function RowChrome({
  tier,
  landmark,
  bare,
  style,
  contentStyle,
  children,
  onFocus,
  onBlur,
  active,
}: ChromeProps) {
  const Root = landmark ? 'footer' : 'div';
  return (
    <Root
      className={`footer-dock-row footer-dock-row--${tier}`}
      data-kb-active={active ? 'true' : undefined}
      style={style}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div aria-hidden="true" className="footer-dock-row__backdrop" />
      <div
        className={
          'footer-dock-row__content' +
          (bare ? '' : ' footer-dock-row__content--dock')
        }
        style={contentStyle}
      >
        {children}
      </div>
    </Root>
  );
}

// The keyboard tier: tracks whether its text input holds focus and reconciles
// itself with the on-screen keyboard via useKeyboardPin. The CSS handles the
// resting position and the data-kb-active lift; useKeyboardPin supplies the iOS
// top-anchor, document pin, and caret re-sync.
function KeyboardRow({
  landmark,
  bare,
  style,
  contentStyle,
  children,
}: Omit<ChromeProps, 'tier' | 'onFocus' | 'onBlur' | 'active'>) {
  const [focused, setFocused] = useState(false);
  const { anchorStyle, correctCaret, keyboardOpen } = useKeyboardPin(focused);

  const handleFocus: React.FocusEventHandler = event => {
    if (isTextInput(event.target)) {
      setFocused(true);
      correctCaret(event.target);
    }
  };
  const handleBlur: React.FocusEventHandler = event => {
    // The keyboard is tied to the text input's focus, so drop focused the moment
    // the input itself blurs — regardless of where focus lands next. (Don't gate
    // on focus leaving the row: the close button can unmount while focused, which
    // fires no focusout, leaving the row stuck "active" and the resting padding
    // collapsed.) The clear button keeps input focus via preventDefault, so
    // tapping it doesn't blur the input.
    if (isTextInput(event.target)) {
      setFocused(false);
    }
  };

  return (
    <RowChrome
      tier="keyboard"
      landmark={landmark}
      bare={bare}
      style={{ ...style, ...anchorStyle }}
      contentStyle={contentStyle}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // Lift the row only once the keyboard has actually opened, not on focus —
      // so a desktop browser (no soft keyboard) never drops it to the bottom.
      active={keyboardOpen}
    >
      {children}
    </RowChrome>
  );
}

/**
 * A full-width footer row. Presentational chrome for the bottom tier; the
 * keyboard tier additionally reconciles itself with the on-screen keyboard.
 * `FooterDock` owns layout and measurement.
 */
export const FooterDockRow: React.FC<FooterDockRowProps> = ({
  children,
  pin = 'bottom',
  landmark = false,
  bare = false,
  style,
  contentStyle,
}) => {
  if (pin === 'keyboard') {
    return (
      <KeyboardRow
        landmark={landmark}
        bare={bare}
        style={style}
        contentStyle={contentStyle}
      >
        {children}
      </KeyboardRow>
    );
  }
  return (
    <RowChrome
      tier="bottom"
      landmark={landmark}
      bare={bare}
      style={style}
      contentStyle={contentStyle}
    >
      {children}
    </RowChrome>
  );
};

export default FooterDockRow;
